import React, { useState, useEffect, useCallback } from 'react';
import { PlusCircle, Edit3, Trash2 } from 'lucide-react';
import { Modal, ConfirmationModal } from './Helpers';
import { fetchDuesDefinitions, addDuesDefinition, updateDuesDefinition, deleteDuesDefinition } from '../api/duesDefinitionApi';
import { fetchExemptionRulesByDefinition } from '../api/exemptionRuleApi';

const DuesDefinitionView = ({ showToast, currentUser }) => {
  const [duesDefinitions, setDuesDefinitions] = useState([]);
  const [definitionName, setDefinitionName] = useState('');
  const [unitPricePerShare, setUnitPricePerShare] = useState('');
  const [unitPricePerConsumption, setUnitPricePerConsumption] = useState('');
  const [fixedAmount, setFixedAmount] = useState('');
  const [definitionType, setDefinitionType] = useState('arsaPayi');
  const [responsibleParty, setResponsibleParty] = useState('residentOrDefaultOwner');
  const [period, setPeriod] = useState('');
  const [totalAmount, setTotalAmount] = useState('');
  const [distributionMethod, setDistributionMethod] = useState('arsaPayi');
  
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingDefinition, setEditingDefinition] = useState(null);
  const [confirmDeleteModal, setConfirmDeleteModal] = useState({ isOpen: false, definitionId: null });
  const [isLoading, setIsLoading] = useState(false);
  const [filterPeriod, setFilterPeriod] = useState(new Date().toISOString().slice(0, 7));
  const [definitionExemptions, setDefinitionExemptions] = useState([]);

  const loadDuesDefinitions = useCallback(async () => {
    if (!currentUser?.buildingId) {
      showToast('Kullanıcıya atanmış bina bulunamadı.', 'error');
      return;
    }
    setIsLoading(true);
    try {
      const data = await fetchDuesDefinitions(currentUser.buildingId, filterPeriod);
      setDuesDefinitions(data || []);
    } catch (error) {
      showToast("Aidat tanımları yüklenirken hata: " + error.message, "error");
      setDuesDefinitions([]);
    } finally {
      setIsLoading(false);
    }
  }, [showToast, currentUser, filterPeriod]);

  useEffect(() => {
    loadDuesDefinitions();
  }, [loadDuesDefinitions]);

  const handleAddOrUpdateDefinition = async (e) => {
    e.preventDefault();
    if (!definitionName.trim() || !period.trim() || !responsibleParty) {
      showToast('Tanım Adı, Dönem ve Sorumlu Taraf alanları zorunludur.', 'error');
      return;
    }
    if (!/^\d{4}-\d{2}$/.test(period)) {
      showToast('Dönem YYYY-AA formatında olmalıdır (örn: 2023-05).', 'error');
      return;
    }

    const definitionData = {
      name: definitionName.trim(),
      type: definitionType,
      unitPricePerShare: parseFloat(unitPricePerShare) || null,
      unitPricePerConsumption: parseFloat(unitPricePerConsumption) || null,
      fixedAmount: parseFloat(fixedAmount) || null,
      responsibleParty,
      period,
      totalAmount: totalAmount ? parseFloat(totalAmount) : null,
      distributionMethod,
      exemptApartmentIds: JSON.stringify([]) // This is now managed in ExemptionRulesView
    };
    setIsLoading(true);
    try {
      if (editingDefinition) {
        await updateDuesDefinition(editingDefinition.id, definitionData);
        showToast('Aidat tanımı başarıyla güncellendi.', 'success');
      } else {
        await addDuesDefinition(definitionData);
        showToast('Aidat tanımı başarıyla eklendi.', 'success');
      }
      loadDuesDefinitions();
      closeModal();
    } catch (error) {
      showToast('Aidat tanımı kaydedilirken hata: ' + error.message, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const openModalForEdit = async (definition) => {
    setEditingDefinition(definition);
    setDefinitionName(definition.name);
    setDefinitionType(definition.type);
    setUnitPricePerShare(definition.unitPricePerShare?.toString() || '');
    setUnitPricePerConsumption(definition.unitPricePerConsumption?.toString() || '');
    setFixedAmount(definition.fixedAmount?.toString() || '');
    setResponsibleParty(definition.responsibleParty || 'residentOrDefaultOwner');
    setPeriod(definition.period);
    setTotalAmount(definition.totalAmount?.toString() || '');
    setDistributionMethod(definition.distributionMethod || 'arsaPayi');
    setIsModalOpen(true);

    try {
        const exemptions = await fetchExemptionRulesByDefinition(definition.id);
        setDefinitionExemptions(exemptions || []);
    } catch (error) {
        showToast("Bu tanıma ait muafiyetler yüklenirken hata oluştu.", "error");
        setDefinitionExemptions([]);
    }
  };

  const openModalForNew = () => {
    setEditingDefinition(null);
    setDefinitionName('');
    setDefinitionType('arsaPayi');
    setUnitPricePerShare('');
    setUnitPricePerConsumption('');
    setFixedAmount('');
    setResponsibleParty('residentOrDefaultOwner');
    setPeriod(filterPeriod); // Default to the currently filtered period
    setTotalAmount('');
    setDistributionMethod('arsaPayi');
    setDefinitionExemptions([]);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingDefinition(null);
    setDefinitionName('');
    setUnitPricePerShare('');
    setUnitPricePerConsumption('');
    setFixedAmount('');
    setTotalAmount('');
    setDefinitionExemptions([]);
  };

  const handleDelete = async (definitionId) => {
    setIsLoading(true);
    try {
      await deleteDuesDefinition(definitionId);
      showToast('Aidat tanımı başarıyla silindi.', 'success');
      loadDuesDefinitions();
    } catch (error) {
      showToast('Aidat tanımı silinirken hata: ' + error.message, 'error');
    } finally {
      setIsLoading(false);
      setConfirmDeleteModal({ isOpen: false, definitionId: null });
    }
  };

  const getResponsiblePartyDisplayName = (rp) => ({'ownerOnly': 'Sadece Kat Maliki', 'residentOrDefaultOwner': 'Sakin (Yoksa Kat Maliki)'}[rp] || rp);
  const getDefinitionTypeDisplayName = (t) => ({'arsaPayi': 'Arsa Paylı', 'sabitTutar': 'Sabit Tutar', 'sayacBazli': 'Sayaç Bazlı'}[t] || t);

  return (
    <div className="bg-white p-6 rounded-lg shadow relative">
      <ConfirmationModal
        isOpen={confirmDeleteModal.isOpen}
        onClose={() => setConfirmDeleteModal({ isOpen: false, definitionId: null })}
        title="Dönemsel Aidatı Sil"
        message="Bu döneme ait aidat tanımını silmek istediğinizden emin misiniz? Bu işlem, bu tanımdan oluşturulmuş borçları etkileyebilir ve geri alınamaz."
        onConfirm={() => handleDelete(confirmDeleteModal.definitionId)}
      />
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold">Dönemsel Aidat Tanımları</h1>
        <button onClick={openModalForNew} className="btn-primary-green-style flex items-center">
          <PlusCircle size={18} className="mr-2"/>Yeni Dönemsel Aidat
        </button>
      </div>

      <div className="grid md:grid-cols-3 gap-4 mb-6 p-4 border rounded-lg bg-gray-50">
        <div>
          <label htmlFor="filterPeriod" className="label-form-style">Döneme Göre Filtrele:</label>
          <input type="month" id="filterPeriod" value={filterPeriod} onChange={(e) => setFilterPeriod(e.target.value)} className="input-form-style" />
        </div>
      </div>
      
      <div className="mb-4 p-4 border rounded-lg bg-yellow-50 text-yellow-800">
        <p><strong>Bilgi:</strong> Bu ekran, seçili döneme ait aidat kalemlerini gösterir. Bunlar, "Aidat Şablonları"ndan otomatik olarak kopyalanan veya bu döneme özel olarak eklediğiniz tek seferlik giderlerdir. Bir şablonun varsayılan değerini kalıcı olarak değiştirmek için "Aidat Şablonları" sayfasını kullanın.</p>
      </div>

      <Modal isOpen={isModalOpen} onClose={closeModal} title={editingDefinition ? "Dönemsel Aidatı Düzenle" : "Yeni Dönemsel Aidat"} size="xl">
        <form onSubmit={handleAddOrUpdateDefinition}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
            <div>
              <label className="label-form-style">Tanım Adı:</label>
              <input type="text" value={definitionName} onChange={(e) => setDefinitionName(e.target.value)} className="input-form-style" required />
            </div>
            <div>
              <label className="label-form-style">Dönem (YYYY-AA):</label>
              <input type="month" value={period} onChange={(e) => setPeriod(e.target.value)} className="input-form-style" required />
            </div>
            <div>
              <label className="label-form-style">Aidat Tipi:</label>
              <select value={definitionType} onChange={(e) => setDefinitionType(e.target.value)} className="input-form-style">
                <option value="arsaPayi">Arsa Payına Göre</option>
                <option value="sabitTutar">Sabit Tutar (Daire Başına)</option>
                <option value="sayacBazli">Sayaç Bazlı</option>
              </select>
            </div>
            <div>
              <label className="label-form-style">Sorumlu Taraf:</label>
              <select value={responsibleParty} onChange={(e) => setResponsibleParty(e.target.value)} className="input-form-style">
                <option value="residentOrDefaultOwner">Sakin (Yoksa Kat Maliki)</option>
                <option value="ownerOnly">Sadece Kat Maliki</option>
              </select>
            </div>
            {/* Value fields based on type */}
          </div>
          <div className="flex justify-end space-x-3 mt-6">
            <button type="button" onClick={closeModal} className="btn-secondary-style">İptal</button>
            <button type="submit" className="btn-primary-green-style" disabled={isLoading}>
              {isLoading ? 'Kaydediliyor...' : (editingDefinition ? "Güncelle" : "Kaydet")}
            </button>
          </div>
        </form>
      </Modal>

      <div className="overflow-x-auto mt-4">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="th-style">Tanım Adı</th>
              <th className="th-style">Dönem</th>
              <th className="th-style">Tip</th>
              <th className="th-style">Değer (TL)</th>
              <th className="th-style">Sorumlu</th>
              <th className="th-style">İşlemler</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {duesDefinitions.map(def => (
              <tr key={def.id}>
                <td className="td-style font-medium">{def.name}</td>
                <td className="td-style">{def.period}</td>
                <td className="td-style">{getDefinitionTypeDisplayName(def.type)}</td>
                <td className="td-style">
                  {def.type === 'arsaPayi' ? (def.totalAmount ? `${def.totalAmount.toFixed(2)} (T)` : def.unitPricePerShare ? `${def.unitPricePerShare.toFixed(2)} /P` : '-') :
                   def.type === 'sabitTutar' ? (def.fixedAmount ? def.fixedAmount.toFixed(2) : '-') :
                   def.unitPricePerConsumption ? `${def.unitPricePerConsumption.toFixed(2)} /B` : '-'}
                </td>
                <td className="td-style">{getResponsiblePartyDisplayName(def.responsibleParty)}</td>
                <td className="td-style space-x-2">
                  <button onClick={() => openModalForEdit(def)} className="icon-button-indigo" title="Düzenle"><Edit3 size={18}/></button>
                  <button onClick={() => setConfirmDeleteModal({isOpen: true, definitionId: def.id})} className="icon-button-red" title="Sil"><Trash2 size={18}/></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {isLoading && duesDefinitions.length === 0 && <p>Yükleniyor...</p>}
        {!isLoading && duesDefinitions.length === 0 && <p className="text-center text-gray-500 py-4">Bu dönem için aidat tanımı bulunmuyor.</p>}
      </div>
    </div>
  );
};

export default DuesDefinitionView;
